import { Component, ElementRef, Inject, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { APP_BASE_HREF } from '@angular/common';

import { Subscription, combineLatest } from 'rxjs';
import { map, tap, filter } from 'rxjs/operators';
import { isEqual } from 'lodash';

import { CoreDialogService } from '../../../Core/Dialogs/services/dialog.service';
import { LocalizationService } from '../../../Core/Localization/services/localization.service';

import { CurrentUserStateService } from '../../../Core/Users/services/current-user-state.service';
import { languageDataLocalizationMap } from '../../../Core/Localization/language-localization.model';

import { buildSlideInAnimationTrigger } from '../../../Core/Dialogs/models/full-profile-animation.model';
import { ConfirmDialogComponent } from '../../../Common/Dialogs/components/ConfirmDialog/confirm-dialog.component';
import { ConfirmDialogModel } from '../../../Common/Dialogs/models/confirm-dialog.model';
import { NewResourceDialogComponent } from '../../../Common/Dialogs/components/NewResourceDialog/new-resource-dialog.component';
import { NewResourceDialogModel } from '../../../Common/Dialogs/models/new-resource-dialog.model';
import { DisplayGroupModel } from '../../../Common/Layout/models/display-group.model';
import { ScrollPanelComponent } from '../../../Common/Basic/components/ScrollPanel/scroll-panel.component';

import { ButtonTypesEnum } from '../../../Common/Forms/enums/button-types.enum';
import { messageSeverityEnum } from '../../../Common/Misc/enums/message-severity.enum';

import { ViDimensionEnum } from '../../enums/vi-dimension.enum';
import { ViMonitorStatusEnum } from '../../enums/vi-monitor-status.enum';

import { ViRoutingService } from '../../services/vi-routing.service';
import { ViMonitorService } from '../../services/vi-monitor.service';
import { ViMonitor } from '../../models/vi-monitor.model';
import { ViMonitorQuestionContext } from '../../models/vi-monitor-question-context.model';
import { AnswerEngineOption, getAllEngineOptions } from '../../models/answer-engine-option.model';

import { ViMonitorSettingsPanelComponent } from '../ViMonitorSettingsPanel/vi-monitor-settings-panel.component';
import { ViBrandSetupPanelComponent } from '../ViBrandSetupPanel/vi-brand-setup-panel.component';
import { ViQuerySetupDialogComponent } from '../ViQuerySetupDialog/vi-query-setup-dialog.component';
import { ViRouteType } from '../../enums/vi-route-type.enum';
interface ViMonitorSetupSectionCardConfig {
    header: string;
    subHeader: string;
    message: string;
    infoMessage?: string;
    warningIconTooltip?: string;
    emptyMessage: string;
    actionLabel: string;
    editActionLabel?: string;
    actionDisabled?: boolean;
    onAction: () => void;
    isFilled?: boolean;
    isStale?: boolean;
    showSummary?: boolean;
}

interface ViQuestionDimensionSummary {
    label: string;
    count: number;
}

@Component({
    selector: 'agpr-vi-monitor-setup',
    templateUrl: './vi-monitor-setup.component.html',
    styleUrls: ['./vi-monitor-setup.component.scss'],
    standalone: false
})
export class ViMonitorSetupComponent implements OnInit, OnDestroy {

    @ViewChild('activateConfirmContent') activateConfirmContent?: TemplateRef<any>;
    @ViewChild('mainScrollPanel') mainScrollPanel?: ScrollPanelComponent;

    monitor: ViMonitor;

    isNew: boolean;

    isLoading: boolean = true;

    displayErrors: boolean = false;
    hasUnsavedChanges: boolean = false;

    monitorName: string = '';

    hasNameRequiredError: boolean = false;

    selectedAnswerEngines: AnswerEngineOption[] = [];

    languageLabel: string = '';

    hasCompetitorsWarning: boolean = false;
    questionDimensionSummary: ViQuestionDimensionSummary[] = [];

    totalActiveQuestions: number = 0;
    totalActiveQuestionsUsed: number = 0;

    remainingActiveQuestionQuota: number = 0;

    activateDisabled: boolean = false;
    activateDisabledTooltip: string = '';

    editDisabled: boolean = false;
    editDisabledTooltip: string = '';

    private overlayContainer: Element | null = null;
    private subscriptions: Subscription[] = [];
    private questionsContextSnapshot: ViMonitorQuestionContext | null = null;
    private uiLanguageTag: string = 'en-US';

    readonly id: string = 'viMonitorSetup';
    readonly buttonTypes = ButtonTypesEnum;
    readonly monitorStatus = ViMonitorStatusEnum;
    readonly messageSeverity = messageSeverityEnum;
    readonly answerEngineOptions: AnswerEngineOption[] = getAllEngineOptions();
    readonly settingsCard: ViMonitorSetupSectionCardConfig = {
        header: 'VisibilityIntelligence.monitorSetup.settings.header',
        subHeader: 'VisibilityIntelligence.monitorSetup.settings.subHeader',
        message: 'VisibilityIntelligence.monitorSetup.settings.message',
        emptyMessage: 'VisibilityIntelligence.monitorSetup.settings.emptyMessage',
        actionLabel: 'VisibilityIntelligence.monitorSetup.settings.action',
        editActionLabel: 'VisibilityIntelligence.monitorSetup.settings.editAction',
        warningIconTooltip: 'VisibilityIntelligence.monitorSetup.settings.action.warningTooltip',
        onAction: () => this.addMonitorSettings()
    };

    readonly brandCard: ViMonitorSetupSectionCardConfig = {
        header: 'VisibilityIntelligence.monitorSetup.brand.header',
        subHeader: 'VisibilityIntelligence.monitorSetup.brand.subHeader',
        message: 'VisibilityIntelligence.monitorSetup.brand.message',
        emptyMessage: 'VisibilityIntelligence.monitorSetup.brand.emptyMessage',
        actionLabel: 'VisibilityIntelligence.monitorSetup.brand.action',
        editActionLabel: 'VisibilityIntelligence.monitorSetup.brand.editAction',
        warningIconTooltip: 'VisibilityIntelligence.monitorSetup.brand.action.warningTooltip',
        onAction: () => this.addBrandDetails()
    };

    readonly questionsCard: ViMonitorSetupSectionCardConfig = {
        header: 'VisibilityIntelligence.monitorSetup.questions.header',
        subHeader: 'VisibilityIntelligence.monitorSetup.questions.subHeader',
        message: 'VisibilityIntelligence.monitorSetup.questions.message',
        emptyMessage: 'VisibilityIntelligence.monitorSetup.questions.emptyMessage',
        actionLabel: 'VisibilityIntelligence.monitorSetup.questions.action',
        editActionLabel: 'VisibilityIntelligence.monitorSetup.questions.editAction',
        showSummary: true,
        onAction: () => this.addQuestions()
    };

    constructor(
        private elementRef: ElementRef,
        private viRoutingService: ViRoutingService,
        private viMonitorService: ViMonitorService,
        private coreDialogService: CoreDialogService,
        private localizationService: LocalizationService,
        private userStateService: CurrentUserStateService,
        @Inject(APP_BASE_HREF) public appBaseHref: string
    ) {
        this.overlayContainer = document.getElementById('main-content');
    }

    ngOnInit() {
        this.subscriptions.push(
            combineLatest([
                this.viRoutingService.getCurrentRoute().pipe(filter(route => !!route)),
                this.viMonitorService.currentResource$.pipe(filter(monitor => !!monitor)),
                this.userStateService.getUILanguageTag()
            ]).subscribe(([route, monitor, tag]) => {
                this.monitor = monitor;
                this.isNew = route.type === ViRouteType.NEW || !monitor?.id;
                this.monitorName = this.monitor.name ?? '';
                this.isLoading = false;
                this.uiLanguageTag = tag;

                this.updateNameValidity();
                this.captureQuestionsSnapshot();
                this.refreshMonitorSettingsCard();
                this.refreshBrandCard();
                this.refreshQuestionsCard();
                this.isLoading = false;

                this.refreshActiveQuestionQuota();
            }),
            this.viMonitorService.getMonitorQuestionLimit().subscribe(limit => {
                this.totalActiveQuestions = limit;
                this.refreshActiveQuestionQuota();
            }),
            this.viMonitorService.totalQuestionsUsed$.subscribe(used => {
                this.totalActiveQuestionsUsed = used;
                this.refreshActiveQuestionQuota();
            }),
            this.viMonitorService.resourceDirty$.subscribe(dirty => {
                this.hasUnsavedChanges = dirty;
                this.updateButtonStates();
            })
        );
    }


    onNameChanged(name: string): void {
        const trimmed = name.trim();
        this.monitorName = trimmed;
        this.monitor.name = trimmed;
        this.updateNameValidity();
        this.viMonitorService.currentResourceModified();
    }

    cancel() {
        if (this.isNew || this.monitor.status === ViMonitorStatusEnum.draft) {
            this.viRoutingService.gotoRoot();
        } else if (this.monitor.status === ViMonitorStatusEnum.paused) {
            this.viRoutingService.gotoReports(this.monitor.id);
        }
    }

    createMonitor() {
        this.displayErrors = true;
        const io: NewResourceDialogModel = {
            inputs: {
                id: 'viMonitorSetup_newMonitor',
                heading: 'VisibilityIntelligence.monitorSetup.newMonitorDialog.heading',
                initialValue: this.monitorName,
                okLabel: 'save',
                observableFormValidator: (name: string) => this.viMonitorService.isNameDuplicated$(name).pipe(
                    map(isDuplicate => isDuplicate ? ['VisibilityIntelligence.monitorSetup.newMonitorDialog.duplicateNameError'] : [])
                ),
                submit: (name: string, group?: DisplayGroupModel) => {
                    this.monitorName = name;
                    this.updateNameValidity();
                    this.monitor.name = name;
                    this.monitor.displayGroupId = group?.id;

                    return this.viMonitorService.create(this.monitor).pipe(
                        tap(id => this.viRoutingService.gotoEdit(id))
                    );
                }
            }
        };

        this.coreDialogService.open(NewResourceDialogComponent, io);
    }


    /** Only available for monitors in draft state */
    saveChanges() {
        this.displayErrors = true;
        if (this.hasNameRequiredError) {
            this.scrollNameFieldIntoView();
            return;
        }

        this.monitor.name = this.monitorName;

        this.viMonitorService.update(this.monitor).subscribe();
    }

    activate(): void {
        this.displayErrors = true;
        if (this.activateDisabled) {
            return;
        }

        const confirmConfig: ConfirmDialogModel = {
            inputs: {
                id: 'viMonitorSetup_activateConfirm',
                heading: 'VisibilityIntelligence.monitorSetup.activateConfirm.title',
                contentTemplate: this.activateConfirmContent,
                okLabel: 'activate',
                maxWidth: 510,
                confirmAction: () => this.viMonitorService.activate(this.monitor.id).pipe(
                    tap(activatedMonitor => this.viRoutingService.gotoReports(activatedMonitor.id))
                )
            },
            outputs: {
                dialogClose: () => {}
            }
        };

        this.coreDialogService.open(ConfirmDialogComponent, confirmConfig);
    }

    /** only available to monitors in the paused state */
    updateMonitor() {
        if (this.editDisabled) {
            return;
        }

        const io: ConfirmDialogModel = {
            inputs: {
                id: this.id + '_editConfirm',
                heading: 'VisibilityIntelligence.monitorSetup.editConfirm.heading',
                message: 'VisibilityIntelligence.monitorSetup.editConfirm.message',
                okLabel: 'update',
                confirmAction: () => {
                    this.monitor.name = this.monitorName;

                    return this.viMonitorService.update(this.monitor).pipe(
                        tap(id => this.viRoutingService.gotoReports(id))
                    );
                }
            },
            outputs: {
                dialogClose: () => {}
            }
        };

        this.coreDialogService.open(ConfirmDialogComponent, io);
    }

    private updateNameValidity(): void {
        this.hasNameRequiredError = !this.monitorName.trim().length;
        this.updateButtonStates();
    }

    private scrollNameFieldIntoView(): void {
        requestAnimationFrame(() => {
            const element: HTMLElement | null = this.elementRef.nativeElement.querySelector(`#${CSS.escape(this.id + '_name')}`);
            if (element) {
                ScrollPanelComponent.scrollElementIntoView(element, this.mainScrollPanel);
            }
        });
    }

    private addMonitorSettings(): void {
        this.coreDialogService.open(ViMonitorSettingsPanelComponent, {
            inputs: {
                animationTrigger: buildSlideInAnimationTrigger(),
                overlayContainer: this.overlayContainer,
                monitor: this.monitor,
                existingQuestionCount: this.monitor.questions?.length ?? 0
            },
            outputs: {
                dialogClose: (confirmed: boolean) => {
                    if (!confirmed) {
                        return;
                    }
                    this.refreshMonitorSettingsCard();
                    this.viMonitorService.currentResourceModified();
                }
            }
        });
    }

    private refreshMonitorSettingsCard(): void {
        this.settingsCard.isFilled = this.monitor.isMonitorSettingsComplete;

        this.selectedAnswerEngines = this.answerEngineOptions.filter(
            option => this.monitor.answerEngines?.includes(option.type)
        );
        this.languageLabel = this.monitor.language
            ? languageDataLocalizationMap[`${this.uiLanguageTag}.${this.monitor.language}`] ?? this.monitor.language
            : '';
        this.syncUpCards();
    }

    private addBrandDetails(): void {
        this.coreDialogService.open(ViBrandSetupPanelComponent, {
            inputs: {
                animationTrigger: buildSlideInAnimationTrigger(),
                overlayContainer: this.overlayContainer,
                monitor: this.monitor,
            },
            outputs: {
                dialogClose: (confirmed: boolean) => {
                    if (!confirmed) {
                        return;
                    }
                    this.refreshBrandCard();
                    this.viMonitorService.currentResourceModified();
                }
            }
        });
    }

    private refreshBrandCard(): void {
        this.brandCard.isFilled = this.monitor.isBrandComplete;
        this.hasCompetitorsWarning = !!this.monitor.competitors?.some(competitor => !competitor.nameVariations?.length);

        this.syncUpCards();
    }

    private addQuestions(): void {
        this.coreDialogService.open(ViQuerySetupDialogComponent, {
            inputs: {
                animationTrigger: buildSlideInAnimationTrigger(),
                overlayContainer: this.overlayContainer,
                monitor: this.monitor
            },
            outputs: {
                dialogClose: (confirmed: boolean) => {
                    if (!confirmed) {
                        return;
                    }
                    this.captureQuestionsSnapshot();
                    this.refreshQuestionsCard();
                    this.viMonitorService.currentResourceModified();
                }
            }
        });
    }

    private refreshQuestionsCard(): void {
        this.questionsCard.isFilled = this.monitor.isQuestionsComplete;
        this.questionDimensionSummary = Object.values(ViDimensionEnum).map(dimension => ({
            label: `VisibilityIntelligence.querySetup.dimensions.${dimension}`,
            count: this.monitor.questions?.filter(question => question.dimension === dimension).length ?? 0
        }));
        this.syncUpCards();
    }

    private captureQuestionsSnapshot(): void {
        this.questionsContextSnapshot = this.monitor.getQuestionRelevantContext();
    }

    private refreshQuestionsStaleWarning(): void {
        this.questionsCard.isStale = !!this.questionsContextSnapshot
            && !!this.monitor.questions?.length
            && !isEqual(this.monitor.getQuestionRelevantContext(), this.questionsContextSnapshot);
    }

    private syncUpCards() {
        const hasSettingsAndBrand = this.settingsCard.isFilled && this.brandCard.isFilled;
        this.questionsCard.infoMessage = !hasSettingsAndBrand ? 'VisibilityIntelligence.monitorSetup.questions.infoMessage': undefined;
        this.questionsCard.actionDisabled = !hasSettingsAndBrand;
        this.refreshQuestionsStaleWarning();
        this.updateButtonStates();
    }

    private refreshActiveQuestionQuota(): void {
        this.remainingActiveQuestionQuota = this.totalActiveQuestions - this.totalActiveQuestionsUsed;
        this.updateButtonStates();
    }

    private updateButtonStates(): void {
        this.updateActivateDisabled();
        this.updateEditDisabled();
    }

    private updateActivateDisabled(): void {
        if (this.hasUnsavedChanges) {
            this.activateDisabled = true;
            this.activateDisabledTooltip = 'VisibilityIntelligence.monitorSetup.activate.disabledTooltip.unsavedChanges';
            return;
        }

        const missingFields = this.getMissingRequiredFieldLabels();
        if (missingFields.length) {
            this.activateDisabled = true;
            this.activateDisabledTooltip = this.localizationService.transform(
                'VisibilityIntelligence.monitorSetup.activate.disabledTooltip.missingFields',
                { fields: missingFields.join(', ') }
            );
            return;
        }

        if (this.hasInsufficientQuota()) {
            this.activateDisabled = true;
            this.activateDisabledTooltip = 'VisibilityIntelligence.monitorSetup.activate.disabledTooltip.quotaExceeded';
            return;
        }

        this.activateDisabled = false;
        this.activateDisabledTooltip = '';
    }

    private updateEditDisabled(): void {
        if (!this.hasUnsavedChanges) {
            this.editDisabled = true;
            this.editDisabledTooltip = 'VisibilityIntelligence.monitorSetup.editMonitor.disabledTooltip.noChanges';
            return;
        }

        const missingFields = this.getMissingRequiredFieldLabels();
        if (missingFields.length) {
            this.editDisabled = true;
            this.editDisabledTooltip = this.localizationService.transform(
                'VisibilityIntelligence.monitorSetup.editMonitor.disabledTooltip.missingFields',
                { fields: missingFields.join(', ') }
            );
            return;
        }

        this.editDisabled = false;
        this.editDisabledTooltip = '';
    }

    /**
     * Return localization keys for each required section not yet complete enough to activate this monitor
     */
    private getMissingRequiredFieldLabels(): string[] {
        const labels: string[] = [];

        if (!this.monitor?.name?.trim()) {
            labels.push('VisibilityIntelligence.monitorSetup.name.label');
        }
        if (!this.monitor?.isMonitorSettingsComplete) {
            labels.push('VisibilityIntelligence.monitorSetup.settings.header');
        }
        if (!this.monitor?.isBrandComplete) {
            labels.push('VisibilityIntelligence.monitorSetup.brand.header');
        }
        if (!this.monitor?.isQuestionsComplete) {
            labels.push('VisibilityIntelligence.monitorSetup.questions.header');
        }

        return labels.map(key => this.localizationService.transform(key));
    }


    private hasInsufficientQuota(): boolean {
        return (this.monitor?.questions?.length ?? 0) > this.remainingActiveQuestionQuota;
    }

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}
